import { getLocalizedDateMask, parseDateInputToDay } from "$root/localization/formatters";
import { normaliseDateEntry } from "@mdotm/shadcn/mdotm/components";

/**
 * What a half-typed day currently amounts to.
 *
 * `incomplete` is its own answer rather than a rejection because it is the state the field is in for
 * every keystroke but the last: telling someone their date is wrong while they are still typing it is
 * how a text field earns the reputation the calendar was supposed to save it from.
 */
export type TypedDay =
	/** Nothing typed: the field is blank, which stages nothing and complains about nothing. */
	| { kind: "empty" }
	/** Fewer than the eight digits a full date needs, or not a real day yet. */
	| { kind: "incomplete" }
	/** A complete, real day the caller's rule will not take. */
	| { kind: "rejected" }
	/** A day the caller will take, in the same `YYYY-MM-DD` form the URL carries. */
	| { kind: "accepted"; date: string };

/**
 * Reads what the user typed into a date field, in their locale's short date format.
 *
 * The caller supplies `accept`, which is its own range rule — the same function that vets its URL
 * param, so what can be typed and what can be linked to are one set by construction rather than two
 * that agree until someone changes one of them.
 *
 * @param typed the field's text, exactly as the user left it
 * @param opts.locale the locale whose short date format the text is read in
 * @param opts.accept the caller's rule: the accepted day, or `null` to reject it
 */
export function parseTypedDay(typed: string, opts: { locale?: string; accept(day: string): string | null }): TypedDay {
	const trimmed = typed.trim();
	if (trimmed === "") {
		return { kind: "empty" };
	}
	// Held back until all eight digits are in. Without this, `20/08/20` parses as the year 20 — a real
	// day, and one every range check refuses — so the hint would flash at whoever types the year last.
	if (trimmed.replace(/\D/g, "").length < 8) {
		return { kind: "incomplete" };
	}
	// The separators can be left out entirely — `08202026` — because putting them in is a parsing
	// concern, not a display one. Doing it here rather than in the field is what lets the field keep the
	// text exactly as typed, and so keep the caret where the user left it.
	const forParsing = normaliseDateEntry(trimmed, getLocalizedDateMask(opts.locale));
	const day = parseDateInputToDay(forParsing, opts.locale);
	if (day == null) {
		return { kind: "incomplete" };
	}
	const accepted = opts.accept(day);
	return accepted == null ? { kind: "rejected" } : { kind: "accepted", date: accepted };
}
